import type { EventEditFormType } from "../schemas/event.edit.form";

type Ticket = NonNullable<EventEditFormType["tickets"]>[number];

const same = (a: number | null | undefined, b: number | null | undefined) =>
  (a ?? null) === (b ?? null);

// The ticket as it was loaded: the one at the same index when its url matches,
// else the one with that url, but only when the url is unique in `before` (two
// tickets sharing a url cannot be told apart).
const findBefore = (before: Ticket[], after: Ticket, index: number) => {
  if (before[index]?.url === after.url) return before[index];
  const matches = before.filter((t) => t.url === after.url);
  return matches.length === 1 ? matches[0] : undefined;
};

const hasPrice = (t: Ticket) =>
  t.price != null || t.minPrice != null || t.maxPrice != null;

/**
 * A curator who edits a ticket price has just confirmed it, so the ticket gets
 * `priceCheckedAt = now`. `price` always equals `minPrice`: whichever of the two
 * the curator edited wins (`minPrice` when both changed). Tickets whose prices
 * did not change are returned untouched, `priceCheckedAt` included.
 */
export function stampCuratorPriceEdits(
  before: Ticket[],
  after: Ticket[],
  now: Date,
): Ticket[] {
  return after.map((ticket, index) => {
    // A changed url has no match by url, so the ticket at the same index is the
    // baseline: a url-only edit leaves the prices, and `priceCheckedAt`, alone.
    // Only a ticket past the end of the loaded list is new; its price is typed.
    const old =
      findBefore(before, ticket, index) ??
      (index < before.length ? before[index] : undefined);
    const changed = (key: "price" | "minPrice" | "maxPrice") =>
      old ? !same(old[key], ticket[key]) : ticket[key] != null;
    const priceChanged = changed("price");
    const minChanged = changed("minPrice");
    if (!priceChanged && !minChanged && !changed("maxPrice")) return ticket;

    const stamped: Ticket = { ...ticket, priceCheckedAt: now.toISOString() };
    if (minChanged) {
      stamped.price = ticket.minPrice;
    } else if (priceChanged || stamped.minPrice == null) {
      stamped.minPrice = ticket.price;
    }
    return stamped;
  });
}
